import _ from 'lodash';
import async from 'async';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

//- material-ui
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import OpenLink from '@material-ui/icons/OpenInNew';
import { Dialog } from '@material-ui/core';
import clsx from 'classnames';

//- UI
import StationInvoiceContainer from '../../containers/accounting/StationInvoice';
import OcrIngestionResultContainer from '../../containers/ocr/OcrIngestionResult';
import InvoiceDocumentContainer from '../../containers/accounting/InvoiceDocument';
import RackTransferContainer from '../../containers/accounting/RackTransfer';

import { getSharedStyles } from '../../themes';
import FormComponent from '../../components/FormComponent';

import FMPSelect from '../../components/pureForm/FMPSelect';
import FMPText from '../../components/pureForm/FMPText';
import FMPDatePicker from '../../components/pureForm/FMPDatePicker';
import FMPCarrierSelect from '../../components/pureForm/FMPCarrierSelect';
import FMPTerminalSupplierPicker from '../../components/pureForm/FMPTerminalSupplierPicker';
import FMPTerminalPicker from '../../components/pureForm/FMPTerminalPicker';
import FMPSwitch from '../../components/pureForm/FMPSwitch';

import GreenChecked from '../../images/icons/greenChecked.svg';
import YellowWarning from '../../images/icons/yellowWarning.svg';
import ErrorIcon from '../../images/icons/error.svg';
import { valueToAbbreviation } from '../../libs/products';
import FMPSectionHeader from '../../components/ui/FMPSectionHeader';
import FMPProductSelect from '../../components/pureForm/FMPProductSelect';
import GenericSavingDialog from '../../components/Dialogs/GenericSavingDialog';
import ConfirmInvalidateApArTriage from '../../components/Dialogs/ConfirmInvalidateApArTriage';
import ChangeDeliveryIdDialog from '../../components/Dialogs/ChangeDeliveryIdDialog';

// const DEFAULT_TERMINAL_SUPPLIER_VALUE = { terminalId: '', supplierId: '' };

const rgbKeyValueColors = [
	{ r: 244, g: 67, b: 54 },
	{ r: 233, g: 30, b: 99 },
	{ r: 156, g: 39, b: 176 },
	{ r: 103, g: 58, b: 183 },
	{ r: 63, g: 81, b: 181 },

	{ r: 33, g: 150, b: 243 },
	{ r: 3, g: 169, b: 244 },
	{ r: 0, g: 188, b: 212 },
	{ r: 0, g: 150, b: 136 },
	{ r: 76, g: 175, b: 80 },

	{ r: 139, g: 195, b: 74 },
	{ r: 205, g: 220, b: 57 },
	{ r: 255, g: 235, b: 59 },
	{ r: 255, g: 193, b: 7 },
	{ r: 255, g: 152, b: 0 },

	{ r: 255, g: 87, b: 34 },
	{ r: 121, g: 85, b: 72 },
];

const documentSettings = [
	{
		documentCategory: 'carrierDeliveryTicket',
		display: 'Carrier Delivery Ticket',
		sort: 100,
		fields: [
			{
				label: 'Carrier',
				name: 'carrierId',
				type: 'carrierSelect',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				label: 'Carrier PO Number',
				name: 'carrierPoNumber',
				type: 'string',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				label: 'Delivery Date',
				name: 'deliveryDate',
				type: 'date',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				label: 'Append To Invoice',
				name: 'appendToInvoice',
				type: 'toggle',
				showForInvoice: true,
				showForRackTransfer: false,
			},
		],
	},
	{
		documentCategory: 'bulkCarrierDeliveryTicket',
		display: 'Carrier Delivery Ticket',
		sort: 105,
		fields: [
			{
				label: 'Carrier',
				name: 'carrierId',
				type: 'carrierSelect',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				label: 'Carrier PO Number',
				name: 'carrierPoNumber',
				type: 'string',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
		],
	},
	{
		documentCategory: `stationDeliveryPickupBol`,
		display: `Station Delivery BOL`,
		sort: 300,
		fields: [
			{
				type: 'productTypeSelect',
				name: 'productType',
				label: 'Product Type',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				type: 'string',
				name: 'bolNumber',
				label: 'BOL Number',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				label: `Terminal / Supplier BOL`,
				type: 'terminalSupplierSelect',
				name: 'terminalSupplier',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				type: 'number',
				name: 'totalAmount',
				label: 'Total Net Fuel',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				type: 'toggle',
				name: 'appendToInvoice',
				label: 'Append To Invoice',
				defaultValue: true,
				showForInvoice: true,
				showForRackTransfer: false,
			},
		],
	},

	{
		documentCategory: `pickupBol`,
		display: `Bulk Pickup BOL`,
		sort: 400,
		productType: 'etoh',
		justBulk: true,
		fields: [
			{
				name: 'productType',
				type: 'productTypeSelect',
				label: 'Product Type',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				name: 'bolNumber',
				type: 'string',
				label: 'BOL Number',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				name: 'terminalSupplier',
				type: 'terminalSupplierSelect',
				label: `Pickup Terminal/Supplier`,
				productType: 'etoh',
				productDisplay: 'Ethanol',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				name: 'netGallons',
				type: 'number',
				label: 'Net Fuel Pickup',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},

			{
				name: 'loadingNumber',
				type: 'string',
				label: 'Loading Number',
				isRequired: true,
				showForInvoice: false,
				showForRackTransfer: true,
			},
			{
				name: 'loadStartDate',
				type: 'date',
				label: 'Pickup Date',
				isRequired: true,
				showForInvoice: false,
				showForRackTransfer: true,
			},
			{
				type: 'toggle',
				name: 'appendToInvoice',
				label: 'Append To Invoice',
				defaultValue: true,
				showForInvoice: true,
				showForRackTransfer: false,
			},
		],
	},

	{
		documentCategory: `offloadBol`,
		display: `Bulk Offload BOL`,
		sort: 400,
		productType: 'etoh',
		justBulk: true,
		fields: [
			{
				type: 'productTypeSelect',
				name: 'productType',
				label: 'Product Type',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				type: 'string',
				name: 'bolNumber',
				label: 'BOL Number',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				label: `Offload Terminal`,
				type: 'terminalSelect',
				name: 'terminalId',
				productType: 'etoh',
				productDisplay: 'Ethanol',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				type: 'number',
				name: 'netGallons',
				label: 'Net Fuel Gals',
				isRequired: true,
				showForInvoice: true,
				showForRackTransfer: true,
			},
			{
				type: 'toggle',
				name: 'appendToInvoice',
				label: 'Append To Invoice',
				defaultValue: true,
				showForInvoice: true,
				showForRackTransfer: false,
			},
		],
	},

	{
		documentCategory: 'other',
		display: 'Other',
		sort: 700,
		fields: [
			{
				label: 'Append To Invoice',
				name: 'appendToInvoice',
				type: 'toggle',
				defaultValue: true,
				showForInvoice: true,
				showForRackTransfer: false,
			},
		],
	},
	{
		documentCategory: 'ignore',
		display: 'Ignore / Duplicate',
		sort: 800,
		fields: [],
	},
];
const documentOptions = documentSettings.map((documentSetting) => {
	const { display, documentCategory } = documentSetting;
	return { display, value: documentCategory };
});

const pBlue = '#0790CB';

const styles = (theme) => ({
	...getSharedStyles(theme),
	block: {
		position: 'absolute',
		borderRadius: '1px',
		boxSizing: 'border-box',
		fontSize: 10,
		cursor: 'pointer',
		transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
		'&:hover': {
			border: '2px solid rgba(3,169,244 ,1)',
			backgroundColor: 'rgba(3,169,244 ,.2)',
		},
	},
	keyBlocks: {
		//border: '2px solid rgba(39,245,74, 1)',
		//backgroundColor: 'rgba(39,245,74 ,.2)',
	},
	blockContainer: {
		borderLeft: '2px solid #bbb',
		padding: `7px 15px 10px 15px`,
		marginBottom: 10,
	},
	addInputButton: {
		borderRadius: '100px',
		marginTop: 20,
	},
	greenCheckedIcon: {
		backgroundImage: `url('${GreenChecked}')`,
		display: 'inline-block',
		width: 80,
		height: 80,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		marginRight: 20,
	},
	greenCheckedIconSmall: {
		backgroundImage: `url('${GreenChecked}')`,
		display: 'inline-block',
		width: 16,
		height: 16,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		marginRight: 2,
	},
	warningIcon: {
		backgroundImage: `url('${YellowWarning}')`,
		display: 'inline-block',
		width: 60,
		height: 60,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		marginRight: 10,
	},
	warningIconSmall: {
		backgroundImage: `url('${YellowWarning}')`,
		display: 'inline-block',
		width: 22,
		height: 22,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		marginRight: 2,
	},
	errorIcon: {
		backgroundImage: `url('${ErrorIcon}')`,
		display: 'inline-block',
		width: 60,
		height: 60,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		marginRight: 10,
	},
	errorIconSmall: {
		backgroundImage: `url('${ErrorIcon}')`,
		display: 'inline-block',
		width: 18,
		height: 18,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		marginRight: 2,
	},

	matchTitle: {
		fontSize: 26,
	},
	sectionContainer: {
		width: '33%',
		marginTop: 20,
		marginBottom: 15,
		fontSize: 18,
		marginRight: 20,
		marginLeft: 20,
	},
	sectionLabel: {
		textTransform: 'uppercase',
		color: '#777',
		marginBottom: 2,
		fontSize: '85%',
		letterSpacing: '1px',
	},
	deliverySection: {
		borderLeft: `2px solid #eee`,
		paddingLeft: 5,
		marginLeft: 5,
	},
	deliveryBottom: {
		marginTop: 5,
		paddingTop: 5,
		borderTop: `1px solid #eee`,
	},
	deliverySelected: {
		borderLeft: `2px solid ${pBlue}`,
	},
	submitButton: {
		fontSize: 18,
		borderRadius: '100px',
		padding: '20px 30px',
		textTransform: 'uppercase',
	},
	submitInvalidButton: {
		fontSize: 14,
		borderRadius: '100px',
		//padding: '20px 30px',
		textTransform: 'uppercase',
		marginRight: 10,
	},
});

class TriageOcrIngestionResult extends FormComponent {
	constructor(props) {
		super(props);

		const ocrIngestionResultId = _.get(this.props, ['match', 'params', 'ocrIngestionResultId']);

		this.state = {
			showBlocks: false,
			showBlockOpen: false,
			showBlockMessage: '',
			ocrIngestionResultId: parseInt(ocrIngestionResultId, 10),
			ocrIngestionDocumentIds: [],
			ocrIngestionDocuments: [],
			isInvalidOpen: false,
			isChangeDeliveryIdOpen: false,
			isSavingOpen: false,
			savingStatus: undefined,
		};
	}

	componentDidMount() {
		const { ocrIngestionResult, ocrIngestionResultGet } = this.props;
		const { ocrIngestionResultId } = this.state;

		if (ocrIngestionResult) {
			ocrIngestionResult.needsRefresh = true;
		}
		ocrIngestionResultGet({
			params: {
				includeBlocks: true,
				ocrIngestionResultIds: [ocrIngestionResultId],
			},
		});
	}

	// eslint-disable-next-line no-unused-vars
	componentDidUpdate(prevProps, prevState, snapshot) {
		if (
			(_.isUndefined(prevProps.ocrIngestionResult) &&
				!_.isUndefined(this.props.ocrIngestionResult)) ||
			prevProps.ocrIngestionResult.needsRefresh
		) {
			console.log('FIlling form');
			const newState = this.fillForm();
			this.setState(newState);
		}
	}

	getBlockSuggestions = () => {
		const { getBlockSuggestions, showErrors } = this.props;
		const { ocrGuideDocumentIds } = this.state;

		getBlockSuggestions({
			params: {
				ocrGuideDocumentIds,
			},
			callback: (err, prepResult) => {
				if (err) {
					return showErrors([err]);
				}

				const { allDocumentResults } = prepResult;

				const firstDocumentResult = allDocumentResults[0];
				const firstResourceBlocks = firstDocumentResult.ocrBlocks;

				const blocksById = _.keyBy(firstResourceBlocks, 'Id');
				const keyBlocks = [];

				//- Lets go through each Blocks
				firstResourceBlocks.forEach((Block) => {
					const { BlockType, Relationships, EntityTypes } = Block;
					if (BlockType !== 'KEY_VALUE_SET') {
						return;
					}

					if (!EntityTypes) {
						return;
					}
					if (_.first(EntityTypes) !== 'KEY') {
						return;
					}

					Block.childBlocks = [];
					Block.childTextString = '';
					Relationships.forEach((relationship) => {
						const { Type, Ids } = relationship;

						if (Type === 'VALUE') {
							const valueBlock = blocksById[_.first(Ids)];
							if (!valueBlock) {
								return;
							}
							if (!valueBlock.Relationships) {
								return;
							}

							valueBlock.childBlocks = [];
							valueBlock.childTextString = '';
							valueBlock.Relationships.forEach((valueRelationship) => {
								if (valueRelationship.Type === 'CHILD') {
									valueRelationship.Ids.forEach((childId) => {
										const childBlock = blocksById[childId];
										valueBlock.childBlocks.push(childBlock);
										valueBlock.childTextString += childBlock.Text;
									});
								}
							});

							Block.valueBlock = valueBlock;
						} else if (Type === 'CHILD') {
							Ids.forEach((childId) => {
								const childBlock = blocksById[childId];
								Block.childBlocks.push(childBlock);
								Block.childTextString += childBlock.Text;
							});
						}
					});

					//- Some don't have values
					if (!Block.valueBlock) {
						return;
					}

					keyBlocks.push(Block);
				});

				this.setState({
					blocksById,
					firstResourceBlocks,
					prepResult,
					keyBlocks,
				});
			},
		});
	};

	fillForm = () => {
		const { ocrIngestionResult, ocrGuideMap, ocrIngestionDocumentMap } = this.props;
		if (!ocrIngestionResult) {
			return;
		}

		// Extract the data from each ocrIngestionDocument for the form

		const { ocrIngestionDocumentIds, matchDataExtractedDocs } = ocrIngestionResult;
		let invoicePages = [];
		ocrIngestionDocumentIds.forEach((ocrIngestionDocumentId) => {
			const ocrDoc = ocrIngestionDocumentMap[ocrIngestionDocumentId];
			if (!ocrDoc) {
				return console.error(`Missing doc: ${ocrIngestionDocumentId}`);
			}
			const { pageNumber, documentResourceId, extractedData, matchedOcrGuideId } = ocrDoc;

			let documentCategory, carrierId, terminalId;

			// Comes from the AI no ocrGuide
			if (extractedData && extractedData.documentCategory) {
				documentCategory = extractedData.documentCategory;
				carrierId = extractedData.carrierId;
				terminalId = extractedData.terminalId;
			} else {
				const ocrGuide = ocrGuideMap[matchedOcrGuideId] || { documentCategory: 'other' };
				({ documentCategory, carrierId, terminalId } = ocrGuide);
			}

			// values stored in the state
			const invoicePage = {
				pageNumber,
				ocrIngestionDocumentId,
				documentResourceId,
				documentCategory,
				carrierId,
				terminalSupplier: { terminalId }, // supplierId comes from extractedDoc
				terminalId, // offload just wants terminal
				extractedInfo: {},
				appendToInvoice: true,
				//...extractedData,
			};

			const extractedDoc = _.find(matchDataExtractedDocs, { ocrIngestionDocumentId }) || {};
			console.log('extractedDoc', extractedDoc);
			// go through fields and add them to the invoice page
			if (extractedDoc && _.isArray(extractedDoc.fields)) {
				extractedDoc.fields.forEach((field) => {
					const { path, value } = field;
					if (path === 'supplierId') {
						invoicePage.terminalSupplier.supplierId = value;
					} else {
						invoicePage[path] = value;
					}
					invoicePage.extractedInfo[path] = field;
				});
			}
			invoicePages.push(invoicePage);
		});
		invoicePages = _.sortBy(invoicePages, 'pageNumber');
		return { invoicePages };
	};

	closeSavingDialog = () => {
		this.setState({ isSavingOpen: false });
	};
	handleContinue = () => {
		const { ocrIngestionResult } = this.props;
		const { matchedStationInvoiceId, matchedRackTransferId } = ocrIngestionResult;
		if (matchedRackTransferId) {
			window.open(`/accounting/rackTransfer/update/${matchedRackTransferId}`, '_blank');
		} else {
			window.open(`/accounting/stationInvoice/update/${matchedStationInvoiceId}`, '_blank');
		}
	};

	handleUpdateInvoice = () => {
		const { showErrors } = this.props;
		this.setState({
			isSavingOpen: true,
			savingStatus: 'saving',
		});

		async.waterfall(
			[
				(callback) => {
					this.saveInvoicePages({ callback });
				},
				(invoiceDocuments, callback) => {
					this.updateInvoice({ invoiceDocuments, callback });
				},
			],
			(err) => {
				if (err) {
					this.setState({
						isSavingOpen: false,
					});
					showErrors([
						`Error during updating invoice. Do not run this again, please check the invoice for updates to finish.`,
						err,
					]);
				}

				this.setState({
					savingStatus: 'done',
				});
			}
		);
	};

	handleUpdateRackTransfer = () => {
		const { showErrors } = this.props;
		this.setState({
			isSavingOpen: true,
			savingStatus: 'saving',
		});

		async.waterfall(
			[
				(callback) => {
					this.saveInvoicePages({ callback });
				},
				(invoiceDocuments, callback) => {
					this.updateRackTransfer({ invoiceDocuments, callback });
				},
			],
			(err) => {
				if (err) {
					this.setState({
						isSavingOpen: false,
					});
					showErrors([
						`Error during updating rack transfer. ` +
							`Do not run this again, please check the rack transfer to finish.`,
						err,
					]);
				}

				this.setState({
					savingStatus: 'done',
				});
			}
		);
	};

	saveInvoicePages = ({ callback }) => {
		const { showErrors } = this.props;
		const { invoicePages } = this.state;

		const invoiceDocuments = [];
		async.eachSeries(
			invoicePages,
			(invoicePage, callback) => {
				this.saveInvoicePage({ invoicePage, invoiceDocuments, callback });
			},
			(err) => {
				if (err) {
					return showErrors([err]);
				}
				return callback(null, invoiceDocuments);
			}
		);
	};

	saveInvoicePage = ({ invoicePage, invoiceDocuments, callback }) => {
		const { ocrIngestionResult, addInvoiceDocument } = this.props;
		const { originalIngestionDocumentResourceId } = ocrIngestionResult;

		const { documentCategory = 'other', appendToInvoice, documentResourceId } = invoicePage;

		let params = {
			documentType: documentCategory,
			pageResourceIds: [documentResourceId],
			originalResourceId: originalIngestionDocumentResourceId,
		};

		// Specific document params
		if (documentCategory === 'stationDeliveryPickupBol') {
			const { productType, terminalSupplier, bolNumber, totalAmount } = invoicePage;
			params.data = {
				productType,
				bolNumber,
				terminalSupplier,
				netGallons: totalAmount,
				appendToInvoice,
			};
		} else if (documentCategory === 'carrierDeliveryTicket') {
			const { carrierPoNumber, deliveryDate, carrierId } = invoicePage;
			params.data = {
				carrierId,
				carrierPoNumber,
				deliveryDate,
				appendToInvoice,
			};
		} else if (documentCategory === 'pickupBol') {
			const {
				productType,
				terminalSupplier,
				bolNumber,
				netGallons,
				loadingNumber,
				loadStartDate,
			} = invoicePage;
			params.data = {
				productType,
				bolNumber,
				terminalSupplier,
				netGallons: parseInt(netGallons),
				loadingNumber,
				appendToInvoice,
				loadStartDate,
			};
		} else if (documentCategory === 'offloadBol') {
			const { productType, terminalId, bolNumber, netGallons } = invoicePage;
			params.data = {
				productType,
				bolNumber,
				terminalId,
				netGallons: parseInt(netGallons),
				appendToInvoice,
			};
		} else {
			params.data = {
				appendToInvoice,
			};
		}

		async.waterfall(
			[
				(callback) => {
					addInvoiceDocument({
						params,
						callback,
					});
				},
				(r, callback) => {
					const invoiceDocument = _.get(r, ['collections', 'invoiceDocumentCollection', 0]);
					if (!invoiceDocument) {
						return callback('Delivery document not returned');
					}

					invoiceDocuments.push(invoiceDocument);
					return callback();
				},
			],
			callback
		);
	};

	updateInvoice = ({ invoiceDocuments, callback }) => {
		const { ocrIngestionResult, showErrors, updateStationInvoice } = this.props;
		const { ocrIngestionResultId, matchedStationInvoiceId } = ocrIngestionResult;

		if (!matchedStationInvoiceId) {
			showErrors(['Did not find a matching station invoice ID to update']);
			return;
		}

		//- Required fields
		const formFieldError = {};
		const errors = [];

		const _setError = (path, error) => {
			_.set(formFieldError, path, error);
			errors.push(error);
		};

		//- Lock and load
		const params = {
			ocrIngestionResultId,
			stationInvoiceId: matchedStationInvoiceId,
			invoiceDocumentIds: [],
			products: {},
		};

		//- Go through the new documents and set any values that are not set already
		const fuelMap = {};
		invoiceDocuments.forEach((invoiceDocument) => {
			const { invoiceDocumentId, documentType, data } = invoiceDocument;
			const { productType } = data;

			//- BOL
			if (productType) {
				const { terminalSupplier, bolNumber, netGallons } = data;
				if (!fuelMap[productType]) {
					fuelMap[productType] = {
						productType,
						terminalSupplier,
						bolNumbers: [],
						netGallons: 0,
					};
				}
				fuelMap[productType].bolNumbers.push(bolNumber);
				fuelMap[productType].netGallons += parseInt(netGallons, 10);
			}

			//- Carrier
			if (documentType === 'carrierDeliveryTicket') {
				const { carrierId, carrierPoNumber, deliveryDate } = data;
				params.carrierId = carrierId;
				params.carrierPoNumber = carrierPoNumber;
				params.deliveryDate = deliveryDate;
				params.issueDate = deliveryDate;
			}
			params.invoiceDocumentIds.push(invoiceDocumentId);
		});

		params.products = _.values(fuelMap);

		//
		// My params are setup now lets verify everything
		//

		//
		let totalGallons = 0;
		params.products.forEach((product, index) => {
			const { terminalSupplier, bolNumbers, netGallons } = product;
			const { terminalId, supplierId } = terminalSupplier;

			if (!terminalId || !supplierId) {
				_setError(['products', index, 'terminalSupplier'], 'Terminal & Supplier required');
			}
			bolNumbers.forEach((bolNumber, bIndex) => {
				if (!bolNumber) {
					_setError(['products', index, 'bolNumbers', bIndex], 'BOL Number required');
				}
			});

			if (!netGallons) {
				_setError(['products', index, 'netGallons'], 'Net Gallons required');
			}

			if (netGallons >= 8800) {
				_setError(
					['products', index, 'netGallons'],
					'Net Gallons is greater than 8800! Probably a typo?'
				);
			}
			totalGallons += parseInt(netGallons, 10);
			if (!_.isNumber(netGallons)) {
				product.netGallons = parseInt(netGallons, 10);
			}
		});

		if (totalGallons >= 10000) {
			return callback(`Total gallons above 10,000. Please recheck your fuel values`);
		}

		let fields = [
			{ fieldName: 'issueDate', display: 'Issue Date' },
			{ fieldName: 'carrierId', display: 'Carrier' },
			{ fieldName: 'carrierPoNumber', display: 'Delivery Ticket Number' },
			{ fieldName: 'deliveryDate', display: 'Delivery Date' },
		];

		fields.forEach((s) => {
			if (_.isNil(params[s.fieldName]) || params[s.fieldName] === '') {
				_setError([s.fieldName], `${s.display} is required`);
			}
		});

		if (!_.isEmpty(errors)) {
			this.setState({ formFieldError });
			return callback(errors);
		}

		/*
		const sortedInvoiceDocuments = _.orderBy(
			invoiceDocuments,
			({ documentType, name }) => {
				if (!documentType) {
					return 99;
				}
				return (documentTypeSort[documentType] || 98) + '_' + name;
			},
			'asc'
		);
		const invoiceDocumentIds = _.compact(_.map(sortedInvoiceDocuments, 'invoiceDocumentId'));
		*/

		async.waterfall(
			[
				(callback) => {
					updateStationInvoice({
						params,
						callback,
					});
				},
			],
			callback
		);
	};

	updateRackTransfer = ({ invoiceDocuments, callback }) => {
		const { ocrIngestionResult, showErrors, updateRackTransfer } = this.props;
		const { ocrIngestionResultId, matchedRackTransferId } = ocrIngestionResult;

		console.log(this.state);
		console.log('INVOICE DOCS', invoiceDocuments);

		if (!matchedRackTransferId) {
			showErrors(['Did not find a matching rack transfer update']);
			return;
		}

		//- Required fields
		const formFieldError = {};
		const errors = [];

		const _setError = (path, error) => {
			_.set(formFieldError, path, error);
			errors.push(error);
		};

		//- Lock and load
		const params = {
			ocrIngestionResultId,
			rackTransferId: matchedRackTransferId,
			invoiceDocumentIds: [],
			//products: {},
		};

		//- Go through the new documents and set any values that are not set already
		let hasOffload = false;
		invoiceDocuments.forEach((invoiceDocument) => {
			const { invoiceDocumentId, documentType, data } = invoiceDocument;

			//- Carrier
			if (documentType === 'carrierDeliveryTicket') {
				const { carrierId, carrierPoNumber } = data;
				params.carrierId = carrierId;
				params.carrierPoNumber = carrierPoNumber;
			} else if (documentType === 'pickupBol') {
				const {
					productType,
					terminalSupplier,
					bolNumber,
					netGallons,
					loadStartDate,
					loadingNumber,
				} = data;
				params.productType = productType;
				params.pickupTerminalId = terminalSupplier.terminalId;
				params.pickupSupplierId = terminalSupplier.supplierId;
				params.pickupBolNumbers = [bolNumber];
				params.pickupNetGallons = parseInt(netGallons, 10);
				params.deliveryDate = loadStartDate;
				params.loadingNumber = loadingNumber;
			} else if (documentType === 'offloadBol') {
				hasOffload = true;
				const { terminalId, bolNumber, netGallons } = data;
				params.offloadTerminalId = terminalId;
				params.offloadBolNumbers = [bolNumber];
				params.offloadNetGallons = parseInt(netGallons, 10);
			}
			params.invoiceDocumentIds.push(invoiceDocumentId);
		});

		if (!params.pickupTerminalId || !params.pickupSupplierId) {
			_setError(['pickupTerminalSupplier'], `Pickup terminal / supplier is required`);
		}

		if (params.pickupNetGallons >= 10000) {
			_setError('pickupNetGallons', `Pickup net gallons above 10,000. Please recheck your values`);
		}

		if (params.offloadNetGallons >= 10000) {
			_setError(
				'offloadNetGallons',
				`Offload net gallons above 10,000. Please recheck your values`
			);
		}

		if (hasOffload) {
			if (_.isEmpty(params.offloadBolNumbers)) {
				_setError('offloadBolNumbers', 'Offload BOL Number required');
			} else {
				params.offloadBolNumbers.forEach((bolNumber, bIndex) => {
					if (!bolNumber) {
						_setError(['offloadBolNumbers', bIndex], 'BOL Number required');
					}
				});
			}
		} else {
			params.offloadNetGallons = 8000;
		}

		if (_.isEmpty(params.pickupBolNumbers)) {
			_setError('pickupBolNumbers', 'Pickup BOL Number required');
		} else {
			params.pickupBolNumbers.forEach((bolNumber, bIndex) => {
				if (!bolNumber) {
					_setError(['pickupBolNumbers', bIndex], 'BOL Number required');
				}
			});
		}

		let fields = [
			{ fieldName: 'carrierId', display: 'Carrier' },
			{ fieldName: 'carrierPoNumber', display: 'Delivery Ticket Number' },
			{ fieldName: 'deliveryDate', display: 'Delivery Date' },
		];

		fields.forEach((s) => {
			if (_.isNil(params[s.fieldName]) || params[s.fieldName] === '') {
				_setError([s.fieldName], `${s.display} is required`);
			}
		});

		if (!_.isEmpty(errors)) {
			this.setState({ formFieldError });
			return callback(errors);
		}
		updateRackTransfer({
			params,
			callback,
		});
	};

	addInput = (arrayName) => {
		this.setState({ [arrayName]: this.state[arrayName].concat(['']) });
	};

	removeInput = (arrayName, index) => {
		const inputs = this.state[arrayName].slice();
		inputs.splice(index, 1);
		this.setState({ [arrayName]: inputs });
	};

	render() {
		const { ocrIngestionResult, classes } = this.props;
		const { invoicePages } = this.state;

		if (!ocrIngestionResult || _.isEmpty(invoicePages)) {
			return <div>Loading...</div>;
		}

		const { matchedStationInvoiceId, matchedRackTransferId } = ocrIngestionResult;

		return (
			<Grid container spacing={2}>
				<Grid item xs={12}>
					{this.renderMatch()}
				</Grid>

				<Grid item xs={12}>
					{invoicePages.map(this.renderPage)}
				</Grid>

				<Dialog
					classes={{ paper: classes.paper }}
					maxWidth="lg"
					open={this.state.showBlockOpen}
					onClose={() => {
						this.setState({ showBlockOpen: false });
					}}
				>
					<pre>{this.state.showBlockMessage}</pre>
				</Dialog>

				<GenericSavingDialog
					open={this.state.isSavingOpen}
					onClose={this.closeSavingDialog}
					onContinue={this.handleContinue}
					processingText={
						matchedRackTransferId
							? 'Processing and updating invoice.'
							: 'Processing and updating rack transfer.'
					}
					successText={
						matchedStationInvoiceId
							? 'Click continue button to go to rack transfer and review and finalize.'
							: 'Click continue button to go to station invoice and finalize.'
					}
					status={this.state.savingStatus}
				/>

				<ConfirmInvalidateApArTriage
					open={this.state.isInvalidOpen}
					onClose={this.closeInvalidDialog}
					ocrIngestionResultId={ocrIngestionResult.ocrIngestionResultId}
					invalidateTriageResult={this.props.ocrIngestionResultUpdate}
					history={this.props.history}
					showErrors={this.props.showErrors}
				/>

				<ChangeDeliveryIdDialog
					open={this.state.isChangeDeliveryIdOpen}
					onClose={this.closeChangeDeliveryIdDialog}
					ocrIngestionResultId={ocrIngestionResult.ocrIngestionResultId}
					overrideMatchData={this.handleOverrideMatchData}
					showErrors={this.props.showErrors}
				/>
			</Grid>
		);
	}

	getDeliveryInfo({ invoiceStationId, stationDeliveryId }) {
		const { stationDeliveryMap, stationMap, carrierMap, classes } = this.props;

		const stationDelivery = stationDeliveryMap[stationDeliveryId];

		if (!stationDelivery) {
			return <div>Problem : Delivery Id: {stationDeliveryId} not found</div>;
		}

		const {
			primaryStationId,
			secondaryStationId,
			primaryDeliveryAmount,
			secondaryDeliveryAmount,
			primaryProductType,
			secondaryProductType,
			carrierId,
			deliveryWindowEndDateTime,
		} = stationDelivery;

		//const productTypeDisplay = valueToDisplay(productType);

		const deliveries = [
			{
				stationId: primaryStationId,
				amount: primaryDeliveryAmount,
				productType: primaryProductType,
			},
		];
		if (secondaryStationId) {
			deliveries.push({
				stationId: secondaryStationId,
				amount: secondaryDeliveryAmount,
				productType: secondaryProductType,
			});
		}
		const carrier = carrierMap[carrierId] || { name: 'MISSING CARRIER' };
		const dateDisplay = moment(deliveryWindowEndDateTime).format('ddd, M/D');

		return (
			<>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
					}}
				>
					Station Delivery #<strong>{stationDeliveryId}</strong>
					<a
						href={`/logistics/stationDelivery/update/${stationDeliveryId}`}
						style={{ textDecoration: 'none', paddingLeft: 5, height: 17 }}
						target="_blank"
						rel="noreferrer noopener"
					>
						<OpenLink style={{ width: 20, height: 20 }} />
					</a>
				</div>
				<div>Carrier : {carrier.name}</div>
				<div>Date : {dateDisplay}</div>

				{deliveries.map(({ stationId, amount, productType }, i) => {
					const station = stationMap[stationId] || { number: '', name: 'Missing station' };
					const destinationDisplay = `${station.number} - ${station.name}`;
					const productTypeAbbreviation = valueToAbbreviation(productType);

					const deliveryClasses = [classes.deliverySection];
					if (i !== 0) {
						deliveryClasses.push(classes.deliveryBottom);
					}

					if (invoiceStationId === stationId) {
						deliveryClasses.push(classes.deliverySelected);
					}
					return (
						<div className={clsx(deliveryClasses)} key={stationId}>
							<div>{destinationDisplay}</div>

							<div>
								{amount} gallons {productTypeAbbreviation}
							</div>
						</div>
					);
				})}
			</>
		);
	}

	getBulkDeliveryInfo({ bulkDeliveryId }) {
		const { bulkDeliveryMap, carrierMap, bulkCustomerMap, terminalMap } = this.props;

		const bulkDelivery = bulkDeliveryMap[bulkDeliveryId];

		if (!bulkDelivery) {
			return <div>Problem : Bulk Delivery Id: {bulkDeliveryId} not found</div>;
		}

		const { carrierId, pickupTerminalId, deliveryBulkCustomerId, deliveryDate } = bulkDelivery;

		const carrier = carrierMap[carrierId] || { name: 'MISSING CARRIER' };
		const bulkCustomer = bulkCustomerMap[deliveryBulkCustomerId] || {
			name: `MISSING BULK CUSTOMER: ${deliveryBulkCustomerId}`,
		};
		const pickupTerminal = terminalMap[pickupTerminalId] || { name: 'MISSING TERMINAL' };
		const dateDisplay = moment(deliveryDate).format('ddd, M/D, YYYY');

		return (
			<>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
					}}
				>
					Bulk Delivery #<strong>{bulkDeliveryId}</strong>
					<a
						href={`/logistics/bulkDelivery/update/${bulkDeliveryId}`}
						style={{ textDecoration: 'none', paddingLeft: 5, height: 17 }}
						target="_blank"
						rel="noreferrer noopener"
					>
						<OpenLink style={{ width: 20, height: 20 }} />
					</a>
				</div>
				<div>Carrier : {carrier.name}</div>
				<div>Pickup Terminal : {pickupTerminal.name}</div>
				<div>Delivery Customer : {bulkCustomer.name}</div>
				<div>Date : {dateDisplay}</div>
			</>
		);
	}

	renderMatch = () => {
		const { ocrIngestionResult, classes } = this.props;
		const {
			matchedStationDeliveryId,
			matchedBulkDeliveryId,
			//matchDataStatus,
			matchDataStatusMessage,
			matchDataExtractedDocs,
		} = ocrIngestionResult;

		if (matchedStationDeliveryId) {
			return this.renderStationMatch();
		} else if (matchedBulkDeliveryId) {
			return this.renderBulkMatch();
		}

		// Nots sure if station or bulk
		let matchInfo;
		if (!matchDataExtractedDocs) {
			matchInfo = <div>Missing matchDataExtractedDocs</div>;
		} else {
			let totalSuccess = 0;
			matchInfo = (
				<div>
					{matchDataStatusMessage && <div>{matchDataStatusMessage}</div>}
					{matchDataExtractedDocs.map(({ ocrIngestionDocumentId, fields }) => {
						return (
							<div key={`doc_${ocrIngestionDocumentId}`}>
								{fields.map((field) => {
									if (field.success) {
										totalSuccess++;
										return null;
									}
									return (
										<div key={`top_${field.path}`}>
											{field.error && (
												<>
													<span className={classes.errorIconSmall} /> : {field.error}
												</>
											)}
											{field.warning && (
												<>
													<span className={classes.warningIconSmall} /> {field.warning}
												</>
											)}
										</div>
									);
								})}
							</div>
						);
					})}
					<div className={classes.greenCheckedIconSmall} /> Found {totalSuccess} matching fields
				</div>
			);
		}

		const title = 'Matching issues. Could not determine station vs bulk delivery.';
		const iconClass = classes.warningIcon;

		return (
			<Paper className={classes.paper}>
				<div>
					<div className={classes.matchTitle}>
						<span className={iconClass} />
						{title}
					</div>

					<div style={{ display: 'flex', width: '100%' }}>
						<div className={classes.sectionContainer}>
							<div className={classes.sectionLabel}>Matching Data</div>
							<div>{matchInfo}</div>
						</div>
					</div>
				</div>
				<div>{this.renderButtons()}</div>
			</Paper>
		);
	};

	renderStationMatch = () => {
		const {
			ocrIngestionResult,
			stationInvoiceMap,
			stationDeliveryMap,
			//carrierMap,
			stationMap,
			classes,
		} = this.props;
		const {
			matchStationDeliveryStatus,
			matchStationDeliveryStatusMessage,
			matchedStationDeliveryId,
			matchStationInvoiceStatus,
			matchStationInvoiceStatusMessage,
			matchedStationInvoiceId,

			matchDataStatus,
			matchDataStatusMessage,
			matchDataExtractedDocs,
		} = ocrIngestionResult;

		const isDeliveryGood = matchStationDeliveryStatus === 'matched';
		const isInvoiceGood = matchStationInvoiceStatus === 'matched';
		const isDataGood = matchDataStatus === 'matched';
		const isGood = isDeliveryGood && isInvoiceGood && isDataGood;

		let deliveryInfo = <div>{matchStationDeliveryStatusMessage}</div>;

		//
		// Invoice matching
		///
		let invoiceMatchStatus;
		let invoiceInfo = <div>No invoiced matched</div>;

		if (matchStationInvoiceStatus === 'matched') {
			invoiceMatchStatus = (
				<div>
					<span className={classes.greenCheckedIconSmall} /> Matched invoice
				</div>
			);
		} else {
			invoiceMatchStatus = (
				<div>
					<div>
						<span className={classes.warningIconSmall} />{' '}
						<strong>{_.startCase(matchStationInvoiceStatus)}</strong>
					</div>
					<div>- {matchStationInvoiceStatusMessage}</div>
				</div>
			);
		}

		let stationIdFromInvoice;
		if (matchedStationInvoiceId) {
			const stationInvoice = stationInvoiceMap[matchedStationInvoiceId];
			if (!stationInvoice) {
				console.error(`Can not find stationInvoice`, matchedStationInvoiceId);
				return;
			}

			const { stationId } = stationInvoice;

			stationIdFromInvoice = stationId;
			const stationDelivery = stationDeliveryMap[matchedStationDeliveryId];
			if (!stationDelivery) {
				console.error(`Can not find stationDeliveryId`, matchedStationDeliveryId);
				return;
			}

			const station = stationMap[stationId] || { number: '', name: 'Missing station' };
			const destinationDisplay = `${station.number} - ${station.name}`;

			const { deliveryWindowEndDateTime } = stationDelivery;
			//const carrier = carrierMap[carrierId] || { name: 'MISSING CARRIER' };
			const dateDisplay = moment(deliveryWindowEndDateTime).format('ddd, M/D');
			invoiceInfo = (
				<div
					style={{
						borderLeft: '2px solid #ddd',
						paddingLeft: 7,
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
						}}
					>
						Station Invoice #<strong>{matchedStationInvoiceId}</strong>
						<a
							href={`/accounting/stationInvoice/update/${matchedStationInvoiceId}`}
							style={{ textDecoration: 'none', paddingTop: 5, paddingLeft: 5 }}
							target="_blank"
							rel="noreferrer noopener"
						>
							<OpenLink style={{ width: 18, height: 18 }} />
						</a>
					</div>
					<div>Station: {destinationDisplay}</div>
					<div>Date : {dateDisplay}</div>
				</div>
			);
		}

		if (matchedStationDeliveryId) {
			deliveryInfo = this.getDeliveryInfo({
				invoiceStationId: stationIdFromInvoice,
				stationDeliveryId: matchedStationDeliveryId,
			});
		}

		let matchInfo;
		if (!matchDataExtractedDocs) {
			matchInfo = <div>Missing matchDataExtractedDocs</div>;
		} else {
			let totalSuccess = 0;
			matchInfo = (
				<div>
					{matchDataStatusMessage && <div>{matchDataStatusMessage}</div>}
					{matchDataExtractedDocs.map(({ ocrIngestionDocumentId, fields }) => {
						return (
							<div key={`doc_${ocrIngestionDocumentId}`}>
								{fields.map((field) => {
									if (field.success) {
										totalSuccess++;
										return null;
									}
									return (
										<div key={`top_${field.path}`}>
											{field.error && (
												<>
													<span className={classes.errorIconSmall} /> : {field.error}
												</>
											)}
											{field.warning && (
												<>
													<span className={classes.warningIconSmall} /> {field.warning}
												</>
											)}
										</div>
									);
								})}
							</div>
						);
					})}
					<div className={classes.greenCheckedIconSmall} /> Found {totalSuccess} matching fields
				</div>
			);
		}

		const title = isGood
			? 'Delivery, Invoice, and Data Matched. Yes!'
			: 'Has matching issues. Please review.';
		const iconClass = isGood ? classes.greenCheckedIcon : classes.warningIcon;

		return (
			<Paper className={classes.paper}>
				<div>
					<div className={classes.matchTitle}>
						<span className={iconClass} />
						{title}
					</div>

					<div style={{ display: 'flex', width: '100%' }}>
						<div className={classes.sectionContainer}>
							<div className={classes.sectionLabel}>Matching Delivery</div>
							<div>{deliveryInfo}</div>
						</div>

						<div className={classes.sectionContainer}>
							<div className={classes.sectionLabel}>Matching Invoice</div>
							<div>{invoiceMatchStatus}</div>
							<div>{invoiceInfo}</div>
						</div>

						<div className={classes.sectionContainer}>
							<div className={classes.sectionLabel}>Matching Data</div>
							<div>{matchInfo}</div>
						</div>
					</div>
				</div>
				<div>{this.renderButtons()}</div>
			</Paper>
		);
	};

	renderBulkMatch = () => {
		const {
			ocrIngestionResult,
			bulkDeliveryMap,
			rackTransferMap,
			bulkCustomerMap,
			// terminalMap,
			// carrierMap,
			// stationMap,
			classes,
		} = this.props;
		const {
			matchBulkDeliveryStatus,
			matchBulkDeliveryStatusMessage,
			matchedBulkDeliveryId,
			matchRackTransferStatus,
			matchRackTransferStatusMessage,
			matchedRackTransferId,

			matchDataStatus,
			matchDataStatusMessage,
			matchDataExtractedDocs,
		} = ocrIngestionResult;

		const isDeliveryGood = matchBulkDeliveryStatus === 'matched';
		const isRackTransferGood = matchRackTransferStatus === 'matched';
		const isDataGood = matchDataStatus === 'matched';
		const isGood = isDeliveryGood && isRackTransferGood && isDataGood;

		let deliveryInfo = <div>{matchBulkDeliveryStatusMessage}</div>;

		//
		// RackTransfer matching
		///
		let rackTransferMatchStatus;
		let deliveryMatchStatus;
		let rackTransferInfo = <div>No rack transfer matched</div>;

		if (matchRackTransferStatus === 'matched') {
			rackTransferMatchStatus = (
				<div>
					<span className={classes.greenCheckedIconSmall} /> Matched rack transfer
				</div>
			);
		} else {
			rackTransferMatchStatus = (
				<div style={{ paddingBottom: '10px' }}>
					<div>
						<span className={classes.warningIconSmall} />{' '}
						<strong>{_.startCase(matchRackTransferStatus)}</strong>
					</div>
					<div style={{ fontSize: '14px', fontStyle: 'italic' }}>
						- {matchRackTransferStatusMessage}
					</div>
				</div>
			);
		}

		if (matchBulkDeliveryStatus === 'matched') {
			deliveryMatchStatus = (
				<div>
					<span className={classes.greenCheckedIconSmall} /> Matched FMP Delivery
				</div>
			);
		} else {
			deliveryMatchStatus = (
				<div style={{ paddingBottom: '10px' }}>
					<div>
						<span className={classes.warningIconSmall} />{' '}
						<strong>{_.startCase(matchBulkDeliveryStatus)}</strong>
					</div>
					<div style={{ fontSize: '15px', fontStyle: 'italic' }}>
						- {matchBulkDeliveryStatusMessage}
					</div>
				</div>
			);
		}

		if (matchedRackTransferId) {
			const rackTransfer = rackTransferMap[matchedRackTransferId];
			if (!rackTransfer) {
				console.error(`Can not find rackTransfer`, matchedRackTransferId);
				return;
			}

			const { bulkCustomerId } = rackTransfer;

			const bulkCustomer = bulkCustomerMap[bulkCustomerId] || {
				name: `MISSING BULK CUSTOMER: ${bulkCustomerId}`,
			};

			const bulkDelivery = bulkDeliveryMap[matchedBulkDeliveryId];
			if (!bulkDelivery) {
				console.error(`Can not find bulkDeliveryId:`, matchedBulkDeliveryId);
				return;
			}

			const { deliveryDate } = bulkDelivery;
			//const carrier = carrierMap[carrierId] || { name: 'MISSING CARRIER' };
			const dateDisplay = moment(deliveryDate).format('ddd, M/D, YYYY');
			rackTransferInfo = (
				<div
					style={{
						borderLeft: '2px solid #ddd',
						paddingLeft: 7,
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
						}}
					>
						Rack Transfer #<strong>{matchedRackTransferId}</strong>
						<a
							href={`/accounting/rackTransfer/update/${matchedRackTransferId}`}
							style={{ textDecoration: 'none', paddingTop: 5, paddingLeft: 5 }}
							target="_blank"
							rel="noreferrer noopener"
						>
							<OpenLink style={{ width: 18, height: 18 }} />
						</a>
					</div>
					<div>Delivery Customer : {bulkCustomer.name}</div>
					<div>Date : {dateDisplay}</div>
				</div>
			);
		}

		if (matchedBulkDeliveryId) {
			deliveryInfo = this.getBulkDeliveryInfo({
				bulkDeliveryId: matchedBulkDeliveryId,
			});
		}

		const matchInfo = this.getMatchDisplayInfo({
			matchDataExtractedDocs,
			matchDataStatusMessage,
			classes,
		});
		const title = isGood
			? 'Delivery, Invoice, and Data Matched. Yes!'
			: 'Has matching issues. Please review.';
		const iconClass = isGood ? classes.greenCheckedIcon : classes.warningIcon;

		return (
			<Paper className={classes.paper}>
				<div>
					<div className={classes.matchTitle}>
						<span className={iconClass} />
						{title}
					</div>

					<div style={{ display: 'flex', width: '100%' }}>
						<div className={classes.sectionContainer}>
							<div className={classes.sectionLabel}>Matching Delivery</div>
							<div>{deliveryMatchStatus}</div>
							<div>{deliveryInfo}</div>
						</div>

						<div className={classes.sectionContainer}>
							<div className={classes.sectionLabel}>Matching Rack</div>
							<div>{rackTransferMatchStatus}</div>
							<div>{rackTransferInfo}</div>
						</div>

						<div className={classes.sectionContainer}>
							<div className={classes.sectionLabel}>Matching Data</div>
							<div>{matchInfo}</div>
						</div>
					</div>
				</div>
				<div>{this.renderButtons()}</div>
			</Paper>
		);
	};

	getMatchDisplayInfo = ({ matchDataExtractedDocs, matchDataStatusMessage, classes }) => {
		let matchInfo;
		if (!matchDataExtractedDocs) {
			matchInfo = <div>Missing matchDataExtractedDocs</div>;
		} else {
			let totalSuccess = 0;
			matchInfo = (
				<div>
					{matchDataStatusMessage && <div>{matchDataStatusMessage}</div>}
					{matchDataExtractedDocs.map(({ ocrIngestionDocumentId, fields }) => {
						return (
							<div key={`doc_${ocrIngestionDocumentId}`}>
								{fields.map((field) => {
									if (field.success) {
										totalSuccess++;
										return null;
									}
									return (
										<div key={`top_${field.path}`}>
											{field.error && (
												<>
													<span className={classes.errorIconSmall} /> : {field.error}
												</>
											)}
											{field.warning && (
												<>
													<span className={classes.warningIconSmall} /> {field.warning}
												</>
											)}
										</div>
									);
								})}
							</div>
						);
					})}
					<div className={classes.greenCheckedIconSmall} /> Found {totalSuccess} matching fields
				</div>
			);
		}
		return matchInfo;
	};

	closeInvalidDialog = () => {
		this.setState({ isInvalidOpen: false });
	};

	closeChangeDeliveryIdDialog = () => {
		this.setState({ isChangeDeliveryIdOpen: false });
	};

	handleOverrideMatchData = ({ params, callback }) => {
		const { ocrIngestionResultOverrideMatchData, ocrIngestionResultGet, showErrors } = this.props;
		const { ocrIngestionResultId } = this.state;

		ocrIngestionResultOverrideMatchData({
			params,
			callback: (err, result) => {
				if (err) {
					callback(err);
					return;
				}

				// refresh the window
				this.setState({ ocrIngestionResult: null });

				// Refresh the ocrIngestionResult to get the updated data
				ocrIngestionResultGet({
					params: {
						includeBlocks: true,
						ocrIngestionResultIds: [ocrIngestionResultId],
					},
					callback: (refreshErr) => {
						if (refreshErr) {
							showErrors([refreshErr]);
						}
						window.location.reload();
						callback(null, result);
					},
				});
			},
		});
	};

	renderButtons() {
		const { ocrIngestionResult, classes } = this.props;
		const isRackTransfer = ocrIngestionResult ? ocrIngestionResult.matchedBulkDeliveryId : false;
		const {
			matchStationDeliveryStatus,
			matchBulkDeliveryStatus,
			matchBulkDeliveryStatusMessage,
			matchStationInvoiceStatus,
			matchRackTransferStatus,
			matchRackTransferStatusMessage,

			invalidReason,
			matchedBulkDeliveryId,
		} = ocrIngestionResult;

		let isButtonDisabled;
		let whyButtonDisabled = 'Disabled';
		if (matchedBulkDeliveryId) {
			const isDeliveryGood = matchBulkDeliveryStatus === 'matched';
			const isRackGood = matchRackTransferStatus === 'matched';
			isButtonDisabled = !isDeliveryGood || !isRackGood;
			whyButtonDisabled =
				matchBulkDeliveryStatusMessage || matchRackTransferStatusMessage || 'disabled';
		} else {
			const isDeliveryGood = matchStationDeliveryStatus === 'matched';
			const isInvoiceGood = matchStationInvoiceStatus === 'matched';
			isButtonDisabled = !isDeliveryGood || !isInvoiceGood;
		}

		let isInvalidButtonDisabled = false;
		if (invalidReason) {
			isButtonDisabled = true;
			isInvalidButtonDisabled = true;
			whyButtonDisabled = invalidReason;
		}

		//isButtonDisabled = false;

		return (
			<div>
				<div
					style={{
						textAlign: 'right',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
					}}
				>
					<Button
						variant="outlined"
						className={classes.submitInvalidButton}
						onClick={() => {
							this.setState({ isInvalidOpen: true });
						}}
						disabled={isInvalidButtonDisabled}
					>
						Mark Invalid
					</Button>

					{isRackTransfer && (
						<Button
							variant="outlined"
							className={classes.submitInvalidButton}
							onClick={() => {
								this.setState({ isChangeDeliveryIdOpen: true });
							}}
						>
							Change Delivery ID
						</Button>
					)}

					{matchedBulkDeliveryId ? (
						<div style={{ display: 'inline-block' }}>
							<Button
								variant="contained"
								color="primary"
								className={classes.submitButton}
								onClick={this.handleUpdateRackTransfer}
								disabled={isButtonDisabled}
							>
								Update Rack Transfer with Data and Documents
							</Button>
							{isButtonDisabled && (
								<div style={{ fontSize: '13px', color: 'gray', paddingTop: 5 }}>
									Disabled Reason: {whyButtonDisabled}
								</div>
							)}
						</div>
					) : (
						<Button
							variant="contained"
							color="primary"
							className={classes.submitButton}
							onClick={this.handleUpdateInvoice}
							disabled={isButtonDisabled}
						>
							Update Invoice with Data and Documents
						</Button>
					)}
				</div>
				{invalidReason && (
					<div style={{ textAlign: 'right', marginTop: 10, padding: 10, border: '1px solid #eee' }}>
						<strong>Invalid Reason:</strong> {invalidReason}
					</div>
				)}
			</div>
		);
	}

	renderPage = (invoicePage, pageIndex) => {
		const { classes, ocrIngestionResult } = this.props;
		const isRackTransfer = ocrIngestionResult ? ocrIngestionResult.matchedBulkDeliveryId : false;

		return (
			<Paper className={classes.paper} key={`page_${pageIndex}`} style={{ marginBottom: 20 }}>
				{pageIndex === 0 && (
					<div style={{ textAlign: 'right' }}>
						<Button
							variant="outlined"
							onClick={() => {
								this.setState({ showBlocks: !this.state.showBlocks });
							}}
						>
							Toggle Debug OCR Blocks
						</Button>
					</div>
				)}

				<Grid container spacing={2}>
					<Grid item xs={4}>
						{this.renderForm({ invoicePage, isRackTransfer, pageIndex })}
					</Grid>
					<Grid item xs={8}>
						{this.renderDocumentResults({ invoicePage, pageIndex })}
					</Grid>
				</Grid>
			</Paper>
		);
	};

	renderForm = ({ invoicePage, isRackTransfer, pageIndex }) => {
		const prefix = `invoicePages.${pageIndex}`;

		const catPath = `${prefix}.documentCategory`;
		let documentCategory = _.get(this.state, catPath);

		const hasDocumentCategory = !!documentCategory;
		let fields;
		if (hasDocumentCategory) {
			if (isRackTransfer && documentCategory === 'carrierDeliveryTicket') {
				documentCategory = 'bulkCarrierDeliveryTicket';
			}
			fields = _.find(documentSettings, { documentCategory })?.fields;
		}

		return (
			<Grid container spacing={2}>
				<FMPSelect {...this.setForm(catPath, 'Document Type', true)} options={documentOptions} />

				{hasDocumentCategory &&
					this.renderFields({
						prefix,
						fields,
						extractedInfo: invoicePage.extractedInfo,
						isRackTransfer,
					})}
			</Grid>
		);
	};

	renderDocumentResults = ({ invoicePage }) => {
		const { classes, ocrIngestionDocumentMap, resourceMap } = this.props;
		const { ocrIngestionDocumentId } = invoicePage;

		const ocrIngestionDocument = ocrIngestionDocumentMap[ocrIngestionDocumentId];
		const {
			documentResourceId,
			ocrBlocks,
			ingestionStatus,
			ingestionStatusMessage,
		} = ocrIngestionDocument;
		const resource = resourceMap[documentResourceId];
		const { url } = resource;
		return (
			<>
				<Paper className={classes.paper}>
					<div style={{ textAlign: 'right' }}>
						<div>Document Ingestion Status: {ingestionStatus}</div>
						{ingestionStatusMessage && <div>{ingestionStatusMessage}</div>}
						<div
							style={{
								boxSizing: 'border-box',
								position: 'relative',
								border: '1px solid gray',
							}}
						>
							<img alt={`doc`} src={url} style={{ width: '100%', opacity: 0.8 }} />

							{this.state.showBlocks && this.renderBlocks(ocrBlocks)}
						</div>
					</div>
				</Paper>
			</>
		);
	};

	renderBlocks = (ocrBlocks) => {
		const { classes } = this.props;

		let keyColorIndex = 0;
		return (
			<>
				{ocrBlocks.map((Block, i) => {
					if (!Block) {
						return null;
					}
					if (Block.BlockType !== 'LINE') {
						return null;
					}

					const box = Block.Geometry.BoundingBox;

					let border, backgroundColor;

					const { r, g, b } = rgbKeyValueColors[keyColorIndex];
					keyColorIndex += 6;
					if (keyColorIndex >= rgbKeyValueColors.length) {
						keyColorIndex = keyColorIndex - rgbKeyValueColors.length;
					}
					border = `1px solid rgba(${r},${g},${b}, 1)`;
					backgroundColor = `rgba(${r},${g},${b} ,.2)`;

					const style = {
						left: box.Left * 100 + '%',
						top: box.Top * 100 + '%',
						width: box.Width * 100 + '%',
						height: box.Height * 100 + '%',
						border,
						backgroundColor,
					};

					return (
						<div
							key={`sg1_${i}`}
							style={style}
							className={classes.block}
							onClick={() => {
								this.setState({
									showBlockOpen: true,
									showBlockMessage: JSON.stringify(Block, undefined, 2),
								});
								// this.handlePickBlock({ Block });
							}}
						/>
					);
				})}
			</>
		);
	};

	renderFields = ({ prefix, fields, extractedInfo, isRackTransfer }) => {
		const { classes } = this.props;
		if (_.isEmpty(fields)) {
			return null;
		}

		return (
			<>
				<Grid item xs={12}>
					<FMPSectionHeader label={'Document Data'} />
				</Grid>

				{fields.map((field, i) => {
					const { name, showForInvoice, showForRackTransfer } = field;

					if (isRackTransfer && !showForRackTransfer) {
						return null;
					}
					if (!isRackTransfer && !showForInvoice) {
						return null;
					}

					let extra = extractedInfo[name] || {};
					if (name === 'terminalSupplier') {
						extra = extractedInfo.supplierId || {};
					}
					const style = { display: 'flex', alignItems: 'center', paddingTop: 10 };
					return (
						<Fragment key={`field_${i}`}>
							{this.renderField({ prefix, field, isRackTransfer })}
							<Grid item xs={12} style={{ paddingTop: 0, fontSize: '90%' }}>
								{extra.error && (
									<div style={style}>
										<span className={classes.errorIconSmall} /> : {extra.error}
									</div>
								)}
								{extra.warning && (
									<div style={style}>
										<span className={classes.warningIconSmall} /> {extra.warning}
									</div>
								)}
								{extra.note && (
									<div style={style}>
										<strong>Note: </strong> {extra.note}
									</div>
								)}
								{extra.success && (
									<div style={style}>
										<span style={{ paddingRight: 5, fontWeight: '600' }}>
											<span className={classes.greenCheckedIconSmall} /> {extra.success}
										</span>
										{extra.successMessage && <span>{extra.successMessage}</span>}
									</div>
								)}
							</Grid>
						</Fragment>
					);
				})}
			</>
		);
	};
	renderField = ({ prefix, field, isRackTransfer }) => {
		let justBulk = false;
		let noFilter = false;
		if (isRackTransfer) {
			justBulk = true;
			noFilter = true;
		}

		const { label, name, type, helpText, defaultValue } = field;
		const path = `${prefix}.${name}`;
		if (type === 'string' || type === 'number') {
			return <FMPText {...this.setForm(path, label)} helpText={helpText} />;
		} else if (type === 'date') {
			return <FMPDatePicker {...this.setForm(path, label)} helpText={helpText} />;
		} else if (type === 'toggle') {
			return (
				<Grid item xs={12}>
					<FMPSwitch {...this.setForm(path, label, false, defaultValue)} />
				</Grid>
			);
		} else if (type === 'terminalSupplierSelect') {
			const { productTypes, productDisplay } = field;
			return (
				<FMPTerminalSupplierPicker
					{...this.setForm(path, `${productDisplay} Terminal`)}
					supplierError={this.getError(path)}
					productTypes={productTypes}
					justBulk={justBulk}
				/>
			);
		} else if (type === 'productTypeSelect') {
			return (
				<FMPProductSelect {...this.setForm(path, label)} productTypes={['e85', 'etoh', 'unl']} />
			);
		} else if (type === 'terminalSelect') {
			const { productTypes } = field;

			return (
				<FMPTerminalPicker
					{...this.setForm(path, label)}
					productTypes={productTypes}
					noFilter={noFilter}
				/>
			);
		} else if (type === 'carrierSelect') {
			return <FMPCarrierSelect {...this.setForm(path, label)} />;
		} else {
			return <div>type : {type}</div>;
		}
	};
}

TriageOcrIngestionResult.propTypes = {
	ocrIngestionResultUpdate: PropTypes.func.isRequired,
	ocrIngestionResultGet: PropTypes.func.isRequired,
	ocrIngestionResultOverrideMatchData: PropTypes.func.isRequired,
	ocrIngestionDocumentMap: PropTypes.object,
	addInvoiceDocument: PropTypes.func.isRequired,

	updateRackTransfer: PropTypes.func.isRequired,
	updateStationInvoice: PropTypes.func.isRequired,

	classes: PropTypes.object.isRequired,
	theme: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
	history: PropTypes.object.isRequired,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	RackTransferContainer,
	StationInvoiceContainer,
	InvoiceDocumentContainer,
	OcrIngestionResultContainer,
])(TriageOcrIngestionResult);
